import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, CheckCircle } from "lucide-react";

export default function ChangePassword() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const isFirstLogin = localStorage.getItem("isFirstLogin") === "true";

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.newPassword !== formData.confirmPassword) {
            toast.error("New passwords do not match");
            return;
        }

        if (formData.newPassword.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                new_password: formData.newPassword
            };

            // Only send old_password if it's NOT a forced first login reset
            // OR if we decided to require it even for first login (which is safer if they know the temp password)
            // The backend requires old_password if NOT is_first_login.
            // If is_first_login, backend allows skipping old_password?
            // Let's check server.py logic:
            // if input.old_password: check it.
            // elif not user.is_first_login: raise Error.
            // So if is_first_login is true, and we don't send old_password, it passes.
            // However, usually for "Force Change", the user knows the temp password, so asking for it is fine.
            // But let's keep it simple: if not first login, send old password.

            if (!isFirstLogin) {
                payload.old_password = formData.oldPassword;
            } else {
                // Ideally we should still ask for it to prevent session hijacking if they walked away?
                // But if they just logged in 1 second ago...
                // Let's send it if they typed it.
                if (formData.oldPassword) {
                    payload.old_password = formData.oldPassword;
                }
            }

            await api.post("/auth/change-password", payload);

            toast.success("Password updated successfully!");

            // Update local storage
            localStorage.setItem("isFirstLogin", "false"); // Clear flag

            // Redirect based on role
            const role = localStorage.getItem("userRole");
            if (role === "coach") navigate("/coach");
            else if (role === "parent") navigate("/parent");
            else navigate("/admin"); // Should not happen for admin usually but safe fallback

        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to update password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
            <Card className="w-full max-w-md shadow-xl">
                <CardHeader className="space-y-1">
                    <div className="flex justify-center mb-4">
                        <div className="p-3 bg-purple-100 rounded-full">
                            <Lock className="w-8 h-8 text-purple-600" />
                        </div>
                    </div>
                    <CardTitle className="text-2xl text-center font-bold">
                        {isFirstLogin ? "Setup Your Password" : "Change Password"}
                    </CardTitle>
                    <CardDescription className="text-center">
                        {isFirstLogin
                            ? "Please set a secure password for your account to continue."
                            : "Enter your current password and a new one below."}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">

                        {/* Show Old Password field if NOT first login, or maybe always show it? 
                If first login, they entered it moments ago. It's redundant but safe.
                Let's hide it for first login to make it smoother, as they just authenticated.
            */}
                        {!isFirstLogin && (
                            <div className="space-y-2">
                                <Label htmlFor="oldPassword">Current Password</Label>
                                <Input
                                    id="oldPassword"
                                    type="password"
                                    value={formData.oldPassword}
                                    onChange={handleChange}
                                    required={!isFirstLogin}
                                    placeholder="••••••"
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="newPassword">New Password</Label>
                            <Input
                                id="newPassword"
                                type="password"
                                value={formData.newPassword}
                                onChange={handleChange}
                                required
                                placeholder="••••••"
                                minLength={6}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm New Password</Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                                placeholder="••••••"
                            />
                        </div>

                        <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading}>
                            {loading ? "Updating..." : "Update Password"}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
